import {
    closestCenter,
    DndContext,
    type DragEndEvent,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import { arrayMove, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { css } from "@emotion/react";
import { Fragment, useLayoutEffect, useState } from "react";

import Button from "../../components/Button/Button";
import EmptyView from "../../components/EmptyView";
import { getSpaceVariable } from "../../components/space/utilities";
import { createListItem, getListItemMarker } from "../listItem";
import {
    blockDefinitions,
    createBlockFrom,
    getBlockTextLength,
    mergeBlocks,
    setBlockContent,
} from "../registry";
import type { Brief, BriefBlock, BriefBlockType } from "../schema";
import type { BlockAutoFocus } from "./blocks/types";
import SortableBlock from "./components/SortableBlock";
import { applyFocus, type CaretContentSplit } from "./utilities/caret";
import { isMeaningfulHtml, type PastedSegment } from "./utilities/paste";

type BriefEditorProps = {
    value: Brief;
    onChange: (brief: Brief) => void;
};

export default function BriefEditor(props: BriefEditorProps) {
    const { value, onChange } = props;
    const blocks = value.blocks;
    const [focus, setFocus] = useState<{ id: string; at: Exclude<BlockAutoFocus, false> } | null>(
        null,
    );
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: { y: 12 } } }),
    );

    const commit = (nextBlocks: BriefBlock[]) => {
        onChange({ ...value, blocks: nextBlocks });
    };

    useLayoutEffect(() => {
        if (!focus) return;
        const element = document.querySelector<HTMLElement>(
            `[data-brief-editor] [data-block-id="${CSS.escape(focus.id)}"] [contenteditable="true"]`,
        );
        if (!element) return;
        applyFocus(element, focus.at);
    }, [focus]);

    const updateBlock = (index: number, block: BriefBlock) => {
        const previous = blocks[index];
        const next = [...blocks];
        next[index] = block;
        commit(next);
        if (previous && previous.type !== block.type) {
            const at = previous.type === "body" || block.type === "body" ? "start" : "end";
            setFocus({ id: block.id, at });
        }
    };

    const removeBlock = (index: number) => {
        commit(blocks.filter((_, current) => current !== index));
    };

    const mergeWithPrevious = (index: number) => {
        const current = blocks[index];
        const previous = index > 0 ? blocks[index - 1] : undefined;
        if (!current || !previous) return;
        const joinOffset = getBlockTextLength(previous);
        const next = [...blocks];
        next[index - 1] = mergeBlocks(previous, current);
        next.splice(index, 1);
        commit(next);
        setFocus({ id: previous.id, at: joinOffset });
    };

    const insertBlock = (index: number, type: BriefBlockType) => {
        const current = blocks[index];
        const created =
            type === "listItem" && current?.type === "listItem"
                ? createListItem({ ordered: current.ordered, depth: current.depth })
                : blockDefinitions[type].create();
        const next = [...blocks];
        next.splice(index + 1, 0, created);
        commit(next);
    };

    const splitBlock = (index: number, split: CaretContentSplit) => {
        const current = blocks[index];
        if (!current) return;

        if (split.position === "start") {
            const created = createBlockFrom(current);
            const next = [...blocks];
            next.splice(index, 0, created);
            commit(next);
            setFocus({ id: created.id, at: "start" });
            return;
        }

        if (split.position === "end") {
            const created = createBlockFrom(current);
            const next = [...blocks];
            next.splice(index + 1, 0, created);
            commit(next);
            setFocus({ id: created.id, at: "start" });
            return;
        }

        const created = createBlockFrom(current, split.after);
        const next = [...blocks];
        next[index] = setBlockContent(current, split.before);
        next.splice(index + 1, 0, created);
        commit(next);
        setFocus({ id: created.id, at: "start" });
    };

    const pasteBlocks = (index: number, segments: PastedSegment[], caret: CaretContentSplit) => {
        const current = blocks[index];
        if (!current || current.type !== "body" || segments.length === 0) return;

        const prepared = segments.map((segment, segmentIndex) => {
            let html = segment.html;
            if (segmentIndex === 0 && segment.type === "body") html = caret.before + html;
            if (segmentIndex === segments.length - 1 && segment.type === "body") {
                html += caret.after;
            }
            return { ...segment, html };
        });

        const pasted: BriefBlock[] = [];
        if (prepared[0]?.type !== "body" && isMeaningfulHtml(caret.before)) {
            pasted.push({ ...current, html: caret.before });
        }
        const pastedBlocks = prepared.map(segmentToBlock);
        pasted.push(...pastedBlocks);
        if (prepared.at(-1)?.type !== "body" && isMeaningfulHtml(caret.after)) {
            pasted.push({ ...blockDefinitions.body.create(), html: caret.after });
        }

        const [first, ...rest] = pasted;
        if (!first) return;
        const next = [...blocks];
        next[index] = first.type === "body" ? { ...current, html: first.html } : first;
        if (rest.length > 0) next.splice(index + 1, 0, ...rest);
        commit(next);

        const focused = pastedBlocks.at(-1);
        if (!focused) return;
        const focusedId =
            focused.id === first.id && first.type === "body" ? current.id : focused.id;
        setFocus({ id: focusedId, at: getBlockTextLength(focused) });
    };

    const addBlock = (type: BriefBlockType) => {
        commit([...blocks, blockDefinitions[type].create()]);
    };

    const onDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = blocks.findIndex((block) => block.id === active.id);
        const newIndex = blocks.findIndex((block) => block.id === over.id);
        if (oldIndex < 0 || newIndex < 0) return;
        commit(arrayMove(blocks, oldIndex, newIndex));
    };

    if (blocks.length === 0) {
        return (
            <EmptyView
                message="Aucun contenu pour le moment"
                callToAction={
                    <div
                        css={css({
                            display: "flex",
                            gap: getSpaceVariable(2),
                        })}
                    >
                        {Object.values(blockDefinitions).map((definition) => (
                            <Button
                                key={definition.type}
                                label={definition.label}
                                action={() => addBlock(definition.type)}
                                feeling="minor"
                                role="minor"
                            />
                        ))}
                    </div>
                }
            />
        );
    }

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <div data-brief-editor="">
                <SortableContext
                    items={blocks.map((block) => block.id)}
                    strategy={verticalListSortingStrategy}
                >
                    {blocks.map((block, index) => (
                        <Fragment key={block.id}>
                            <SortableBlock
                                block={block}
                                previousBlock={index > 0 ? blocks[index - 1] : undefined}
                                onChange={(next) => updateBlock(index, next)}
                                controls={{
                                    splitBlock: (split) => splitBlock(index, split),
                                    mergeWithPrevious: () => mergeWithPrevious(index),
                                    pasteBlocks: (segments, caret) =>
                                        pasteBlocks(index, segments, caret),
                                    closeOptions: () => {},
                                    insertBlock: (type) => insertBlock(index, type),
                                    removeBlock: () => removeBlock(index),
                                }}
                                autoFocus={focus?.id === block.id ? focus.at : false}
                                listMarker={
                                    block.type === "listItem"
                                        ? getListItemMarker(blocks, index)
                                        : ""
                                }
                            />
                        </Fragment>
                    ))}
                </SortableContext>
            </div>
        </DndContext>
    );
}

function segmentToBlock(segment: PastedSegment): BriefBlock {
    if (segment.type === "listItem") {
        return createListItem({
            html: segment.html,
            ordered: segment.ordered,
            depth: segment.depth,
        });
    }
    return { ...blockDefinitions.body.create(), html: segment.html };
}
